import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import BookingBenifitsSection from '~/components/experience-details-page/booking-benifits-section';
import Layout from '~/components/layout';
import { useTranslate } from '~/i18n';
import Head from 'next/head';
import SuggestedExperiences from '~/components/experience-details-page/suggested-experiences';
import { ImageWithPlaceholder } from '~/utils/image-plaiceholder';
import { BASE_URL, isJarir } from '~/utils/theme';
import { EventSchema } from '~/components/common/page-schema';
import { experienceDetailsGetStaticProps } from '~/utils/static-props/experience-details-ssg';
import { GetStaticPaths } from 'next';
import { ExperienceDetailsContent } from '~/components/experience-details-page';
import { useEffect } from 'react';
import { expDetailsPageViewEvent } from '~/utils/mixpanel-events/experience-details';

interface ExperienceDetailsProps {
   data: any;
   reviews?: any;
   suggestedExp?: any;
}

const ExperienceDetailsPage = ({ data, reviews, suggestedExp }: ExperienceDetailsProps) => {
   const { lang, translate, locale } = useTranslate();
   const router = useRouter();
   const { category, slug, city } = router.query;
   const lang_key = lang === 'sa-en' ? 'en' : 'ar';

   useEffect(() => {
      if (data) expDetailsPageViewEvent(data);
   }, [data]);

   if (!data) return null;

   const oppositeLanguage = locale === 'ar' ? 'en' : 'ar';
   const langHref = `/experiences/${city}/${data.category?.[`slug_${oppositeLanguage}`]}/${slug}`;

   const startDate = data.booking_dates_arr?.[0];
   const endDate = data.booking_dates_arr?.[data.booking_dates_arr.length - 1];

   return (
      <Layout langHref={langHref}>
         <style jsx global>{`
            #__next main {
               overflow-x: unset !important;
               background-color: white;
            }
         `}</style>
         {startDate && (
            <EventSchema
               startDate={startDate}
               endDate={endDate}
               // Rich snippet breaks with (")
               name={data.name.replace(/"/g, "'")}
               description={data.description}
               image={data.venue_image}
               eventAttendanceMode={
                  category === 'online' || category === 'اونلاين' ? 'online' : 'offline'
               }
               url={BASE_URL + router.asPath}
               offers={{
                  price: data.discounted_price ?? data.list_price,
                  priceCurrency: 'SAR',
                  url: BASE_URL + router.asPath,
                  validFrom: startDate || '',
               }}
               organizer={{
                  name: data.organizer_name,
                  url: BASE_URL + router.asPath,
               }}
               location={{
                  name: data.name,
                  address: {
                     streetAddress: data.address,
                     addressCountry: data.country,
                     addessLocality: data.city,
                  },
               }}
            />
         )}

         {/* SEO */}
         <NextSeo
            titleTemplate="%s"
            title={
               data[`meta_title_${lang_key}`] ||
               translate({ id: 'seo:page title', values: { title: data.name } })
            }
            description={data[`meta_description_${lang_key}`]}
            additionalMetaTags={[{ name: 'keywords', content: data[`meta_keywords_${lang_key}`] }]}
            openGraph={{
               images: data.optimized_images?.map(({ img }: ImageWithPlaceholder) => ({
                  url: img.src,
                  alt: translate({ id: 'seo:home alt' }),
               })) || [{ url: data.venue_image, alt: translate({ id: 'seo:home alt' }) }],
            }}
         />
         <Head>
            {!isJarir && (
               <meta
                  name="apple-itunes-app"
                  content={`app-id=1097399425, app-argument=https://app.halayalla.com/venues/detail/${data.venue_id}`}
               />
            )}
         </Head>
         <div className="relative">
            <ExperienceDetailsContent
               data={{
                  ...data,
                  address: data.noem_address || data.city, // Neom Address fix for neom-the-line-dhahran,
               }}
               reviews={reviews}
            />
            <SuggestedExperiences suggestedExp={suggestedExp} />
            <BookingBenifitsSection />
         </div>
      </Layout>
   );
};

export const getStaticPaths: GetStaticPaths = async () => {
   return {
      paths: [],
      fallback: 'blocking',
   };
};
export const getStaticProps = experienceDetailsGetStaticProps;

ExperienceDetailsPage.hideLayout = true;

export default ExperienceDetailsPage;
